if (!customElements.get('newsletter-signup')) {
  class NewsletterSignup extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.form.dataset.newsletterBound === 'true') return;

      this.form.dataset.newsletterBound = 'true';
      this.feedback = this.form.querySelector('[data-newsletter-feedback]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.successMessage = this.dataset.successMessage || 'Thanks for subscribing';

      this.boundSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this.boundSubmit, true);
      this.stripNewsletterQueryParams();
    }

    disconnectedCallback() {
      if (this.form && this.boundSubmit) {
        this.form.removeEventListener('submit', this.boundSubmit, true);
      }
    }

    stripNewsletterQueryParams() {
      if (!window.history.replaceState) return;

      const url = new URL(window.location.href);
      if (!url.searchParams.has('newsletter_success')) return;

      url.searchParams.delete('newsletter_success');
      url.searchParams.delete('customer_posted');
      url.searchParams.delete('form_type');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    setBusy(isBusy) {
      if (this.submitButton) {
        this.submitButton.toggleAttribute('disabled', isBusy);
        this.submitButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }
    }

    blockClicksTemporarily() {
      this.setAttribute('data-newsletter-submitted', 'true');
      const duration = window.theme?.config?.isTouch ? 1500 : 600;
      window.setTimeout(() => {
        this.removeAttribute('data-newsletter-submitted');
      }, duration);
    }

    renderAlert(status, content) {
      if (!this.feedback) return;

      this.feedback.innerHTML = `
        <div class="alert alert--${status} flex items-start gap-3 text-sm md:text-base leading-tight" role="status">
          ${content}
        </div>
      `;
      this.feedback.hidden = false;
    }

    showSuccess() {
      this.renderAlert('success', this.escapeHtml(this.successMessage));
      this.form.reset();
      this.blockClicksTemporarily();
    }

    showError(message) {
      this.renderAlert('error', message);
    }

    escapeHtml(value) {
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    buildPostBody() {
      const body = new FormData(this.form);
      body.delete('return_to');
      return body;
    }

    isSuccessResponse(html, response) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedForm = doc.getElementById(this.form.id);
      const alert = fetchedForm?.querySelector('[data-newsletter-feedback] .alert');

      if (alert?.classList.contains('alert--error')) {
        this.showError(alert.innerHTML);
        return true;
      }

      if (
        alert?.classList.contains('alert--success') ||
        html.includes('customer_posted') ||
        html.includes('posted_successfully') ||
        response.redirected ||
        response.ok
      ) {
        this.showSuccess();
        return true;
      }

      return false;
    }

    async onSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (this.form.dataset.submitting === 'true') return;
      this.form.dataset.submitting = 'true';
      this.setBusy(true);

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: this.buildPostBody(),
          credentials: 'same-origin',
          redirect: 'follow',
          headers: {
            Accept: 'text/html',
          },
        });

        const html = await response.text();

        if (!this.isSuccessResponse(html, response)) {
          this.showSuccess();
        }
      } catch (error) {
        this.showError(this.escapeHtml('Something went wrong. Please try again.'));
      } finally {
        this.form.dataset.submitting = 'false';
        this.setBusy(false);
      }
    }
  }

  customElements.define('newsletter-signup', NewsletterSignup);
}
